<template>
  <div class="add-matrix">
    <div class="file-block">
      <div v-show="!selectedMatrix" class="set-file-btn">
        <label for="file-input-matrix" class="file-label">Добавить ИМ</label>
        <input id="file-input-matrix" type="file" ref="fileInputMatrix" accept=".xlsx" class="file-input-matrix" @change="handleFileChange" />
      </div>
      <p v-if="selectedMatrix" class="file-name">{{ selectedMatrix }}</p>
      <button
        v-if="selectedMatrix"
        @click="submitMatrix"
        class="upload-button"
        :disabled="uploading">{{ uploading ? 'Отправка…' : 'Отправить' }}</button>
    </div>
    <p class="hint">
      Новая матрица не стирает прежнюю: прежняя остаётся ниже с пометкой «прошлая».
      Если ИМ пришла на почту от Эфеса, система загрузит её сама.
    </p>

    <div v-if="lastResult" :class="['result', lastResult.ok ? 'ok' : 'bad']">{{ lastResult.text }}</div>

    <div class="versions" v-if="versions.length">
      <h4 class="versions-title">Индивидуальные матрицы</h4>
      <div v-for="v in versions" :key="v.id" :class="['version', v.is_active ? 'active' : 'past']">
        <div class="version-head">
          <span :class="['badge', v.is_active ? 'badge-active' : 'badge-past']">{{ v.is_active ? 'Действующая' : 'Прошлая' }}</span>
          <span class="period">{{ periodText(v) }}</span>
        </div>
        <div class="version-meta">
          Маршрутов: {{ v.rows_loaded }}<span v-if="v.rows_skipped"> · не распознано строк: {{ v.rows_skipped }}</span>
          · загружена {{ v.source_title }}<span v-if="v.uploaded_at"> {{ dt(v.uploaded_at) }}</span>
          <span v-if="v.mail_subject"> · письмо «{{ v.mail_subject }}»</span>
        </div>
        <div class="version-meta" v-if="v.changes && (v.changes.added || v.changes.removed || v.changes.changed)">
          К прошлой: новых маршрутов {{ v.changes.added }}, пропало {{ v.changes.removed }}, изменён тариф {{ v.changes.changed }}
        </div>
        <button v-if="v.has_file" class="link-button" @click="download(v)">Скачать файл {{ v.file_name || '' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import ApiConnector from '@/backend/apiModels/userApi'
import { createToaster } from "@meforma/vue-toaster";

// [Claude] 04.11.2026: версии ИМ — действующая и прошлые, файл каждой можно скачать;
// причина отказа показывается на экране, а не «Ошибка загрузки».
export default {
  name: 'CustomerMatrix',
  props: ['customerId'],
  setup(props) {
    const fileInputMatrix = ref(null);
    const selectedMatrix = ref('');
    const uploading = ref(false)
    const versions = ref([])
    const lastResult = ref(null)
    const api = new ApiConnector()
    const toaster = createToaster()

    const dt = (value) => {
      if (!value) return ''
      const d = new Date(value)
      return isNaN(d) ? '' : d.toLocaleDateString('ru-RU')
    }
    const periodText = (v) => {
      if (v.period_from) return 'Период ' + dt(v.period_from) + (v.period_to ? ' – ' + dt(v.period_to) : '')
      if (v.is_active) return 'Действует с ' + dt(v.valid_from)
      return 'Действовала ' + dt(v.valid_from) + (v.valid_to ? ' – ' + dt(v.valid_to) : '')
    }

    const loadVersions = async () => {
      try {
        const { response, request } = api.getDatas('customers/' + props.customerId + '/matrix/versions')
        await request()
        versions.value = Array.isArray(response.value) ? response.value : []
      } catch {
        versions.value = []
      }
    }

    const handleFileChange = () => {
      const file = fileInputMatrix.value.files[0];
      selectedMatrix.value = file ? file.name : '';
    };

    const errorText = (e) => {
      const detail = e?.response?.data?.detail
      if (typeof detail === 'string' && detail) return detail
      if (e?.response?.status === 413) return 'Файл слишком большой'
      return 'ИМ не загружена: нет связи с сервером. Попробуйте ещё раз'
    }

    const submitMatrix = async () => {
      const file = fileInputMatrix.value.files[0];
      if (!file || uploading.value) return
      uploading.value = true
      try {
        const { response, request } = api.updateCustomerTable('customers/matrix/' + props.customerId, file);
        await request();
        if (response.value?.msg) {
          lastResult.value = { ok: true, text: response.value.msg }
          toaster.success(response.value.msg)
        } else {
          lastResult.value = { ok: false, text: 'Не удалось загрузить ИМ' }
          toaster.error('Не удалось загрузить ИМ')
        }
      } catch (e) {
        const text = errorText(e)
        lastResult.value = { ok: false, text }
        toaster.error(text)
      } finally {
        uploading.value = false
        if (fileInputMatrix.value) fileInputMatrix.value.value = ''
        selectedMatrix.value = ''
        loadVersions()
      }
    }

    const download = async (v) => {
      try {
        const res = await api.api.get('customers/' + props.customerId + '/matrix/versions/' + v.id + '/file', { responseType: 'blob' })
        const url = URL.createObjectURL(res.data)
        const a = document.createElement('a')
        a.href = url
        a.download = v.file_name || 'ИМ_' + dt(v.valid_from) + '.xlsx'
        document.body.appendChild(a)
        a.click()
        a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 5000)
      } catch {
        toaster.error('Файл этой матрицы не сохранён')
      }
    }

    onMounted(loadVersions)

    return {
      fileInputMatrix,
      selectedMatrix,
      uploading,
      versions,
      lastResult,
      handleFileChange,
      submitMatrix,
      download,
      periodText,
      dt,
    };
  },
};
</script>

<style scoped>
.add-matrix {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-block {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.file-label,
.upload-button {
  padding: 8px 16px;
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.file-label:hover,
.upload-button:hover {
  background-color: #0056b3;
}

.upload-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.file-input-matrix {
  display: none;
}

.file-name {
  margin: 0;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hint {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.result {
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.result.ok {
  background: #ecfdf3;
  color: #166534;
}

.result.bad {
  background: #fef2f2;
  color: #991b1b;
}

.versions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}

.versions-title {
  margin: 4px 0 0;
}

.version {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.version.active {
  border-color: #86efac;
  background: #f6fef9;
}

.version.past {
  background: #fafafa;
}

.version-head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.badge {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 999px;
}

.badge-active {
  background: #16a34a;
  color: #fff;
}

.badge-past {
  background: #e5e7eb;
  color: #374151;
}

.period {
  font-weight: 600;
}

.version-meta {
  font-size: 12px;
  color: #4b5563;
}

.link-button {
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  color: #007bff;
  cursor: pointer;
  font-size: 13px;
}

.link-button:hover {
  text-decoration: underline;
}
</style>
